/**
 * useChartMindSession - Master hook for ChartMind session state management
 * 
 * Composes all ChartMind hooks and lifts component state into a single
 * coordinated session that can be serialized for Firestore persistence.
 * 
 * Phase 1: Hook composition and state consolidation
 * Phase 2: Serialization layer (serializeSession, hydrateSession)
 * Phase 3: Firestore integration (saveSession, loadSession)
 */

import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useDebounce, useDebouncedCallback } from "use-debounce";
import { formatISO } from "date-fns";
// Server-side transcription (OpenAI under our BAA) rather than the browser's Web Speech API.
// Swapped together with the phone page on purpose: two recording surfaces on two different
// engines would mean two accuracy profiles and two sets of browser rules to explain.
// useVoiceRecording is still in the tree, so this is a one-line revert.
import useEncounterRecording from "hooks/useEncounterRecording";
import usePhoneRecordingRelay from "hooks/usePhoneRecordingRelay";
import useChartMind from "hooks/useChartMind";
import useClarifyingQuestions from "hooks/useClarifyingQuestions";
import useChartGeneration from "hooks/useChartGeneration";
import useDiagnosticPlan from "hooks/useDiagnosticPlan";
import useConfirmDiagnosis from "hooks/useConfirmDiagnosis";
import useTreatmentPlan from "hooks/useTreatmentPlan";
import useChartMindDischarge from "hooks/useChartMindDischarge";
import { useAuth } from "hooks/useAuth";
import { STEPS } from "components/dashboard/chartmind/ChartMindHeader";
import * as sessionService from "services/chartMindSessionService";
import {
  downloadBlob,
  exportClinicalNotePdf,
} from "services/documentsService";
import { compactSourceList } from "utils/clinicalSources";
import {
  buildPatientContextPayload,
  patientContextPayloadKey,
} from "utils/patientContext";
import { djb2 } from "utils/hash";
import {
  PEARLS_PROMPT_ID,
  generatePearls,
  isMissingPromptError,
} from "components/dashboard/chartmind/pearls";
import { isSameCondition } from "utils/conditionMatch";
import {
  EVENTS_PLAN_FOCUS,
  INPATIENT_FALLBACK_INSTRUCTIONS,
  NOTE_FORMATS,
  normalizeNoteFormat,
  buildEventsNoteContext,
  buildEventsSourceText,
  buildInpatientNoteContext,
  createInpatientEvent,
  ensurePendingInNote,
  isEventsNote,
  isInpatient,
  parseEventTime,
  normalizeEncounterSetting,
  normalizeInpatientEvents,
  pendingDailyPlanNames,
} from "components/dashboard/chartmind/inpatient";
import {
  formatDocumentationOutline,
  normalizeSpecialty,
  noteTemplateFor,
  promptOptions,
  specialtyFocus,
} from "components/dashboard/chartmind/specialties";

// Imported patient records are redeemed under a 60-minute, section-scoped consent. Persisting
// the clinical blob in the session document outlives that consent window, so it stays off
// until the client signs off on it. The serialize and hydrate paths below are complete: flip
// this one constant to true and the record survives reloads with no other change.
const PERSIST_IMPORTED_PATIENT_RECORD = false;

// The clinician's last inpatient/outpatient choice, used as the default for new encounters.
// localStorage like the other `chartmind:` prefs; nothing here is PHI.
const ENCOUNTER_SETTING_KEY = "chartmind:encounter-setting";
const NOTE_FORMAT_KEY = "chartmind:note-format";
const SPECIALTY_KEY = "chartmind:specialty";
const readDefaultSpecialty = () => {
  try {
    return normalizeSpecialty(window.localStorage.getItem(SPECIALTY_KEY));
  } catch {
    return normalizeSpecialty(null);
  }
};
const readDefaultNoteFormat = () => {
  try {
    return normalizeNoteFormat(window.localStorage.getItem(NOTE_FORMAT_KEY));
  } catch {
    return normalizeNoteFormat(null);
  }
};
const readDefaultEncounterSetting = () => {
  try {
    return normalizeEncounterSetting(window.localStorage.getItem(ENCOUNTER_SETTING_KEY));
  } catch {
    return normalizeEncounterSetting(null);
  }
};

/**
 * useChartMindSession Hook
 * 
 * @param {string|null} initialSessionId - Optional session ID to load on mount
 * @param {Object} [options]
 * @param {boolean} [options.disableSave=false] - [BETA-KIJABE] When true, all Firestore
 *   persistence is nooped (no auto-save, no manual save, no session ID creation).
 *   Used for Kijabe beta testers so encounters aren't persisted. Remove when beta ends.
 * @returns {Object} Grouped session state and actions
 */
function useChartMindSession(initialSessionId = null, { disableSave = false } = {}) {
  // Get current user for session persistence
  const { user, organizationId, isGlobalAdmin } = useAuth();

  // ============================================================================
  // COMPOSE EXISTING HOOKS
  // ============================================================================
  
  // Note: onDataChanged callbacks will be defined after autoSaveSession is created
  // We'll use a ref to avoid circular dependencies
  const onDataChangedRef = useRef(null);

  // Stable callback that delegates to the ref — must NOT be recreated on every render.
  // Each inline `() => onDataChangedRef.current?.()` would be a new function reference,
  // causing every useCallback inside the sub-hooks (e.g. analyzeDDX) to also change,
  // which makes the auto-analysis effect deps change every render → infinite LLM loop.
  const stableOnDataChanged = useCallback(() => onDataChangedRef.current?.(), []);
  
  const [recordingLanguage, setRecordingLanguageState] = useState(() => {
    try {
      return window.localStorage.getItem("chartmind:recording-language") || "en-US";
    } catch {
      return "en-US";
    }
  });
  const setRecordingLanguage = useCallback((lang) => {
    setRecordingLanguageState(lang);
    try {
      window.localStorage.setItem("chartmind:recording-language", lang);
    } catch {
      // ignore
    }
  }, []);

  // Inpatient or outpatient. The last choice is the default for the next encounter; a saved
  // session restores its own setting without changing that default.
  const [encounterSetting, setEncounterSettingState] = useState(readDefaultEncounterSetting);
  const setEncounterSetting = useCallback((value) => {
    const setting = normalizeEncounterSetting(value);
    setEncounterSettingState(setting);
    try {
      window.localStorage.setItem(ENCOUNTER_SETTING_KEY, setting);
    } catch {
      // ignore
    }
  }, []);
  const inpatient = isInpatient(encounterSetting);

  // Specialty (ChartMind-7's six), remembered the same way. It picks the note outline and the
  // focus text every step's request carries.
  const [specialty, setSpecialtyState] = useState(readDefaultSpecialty);
  const setSpecialty = useCallback((value) => {
    const next = normalizeSpecialty(value);
    setSpecialtyState(next);
    try {
      window.localStorage.setItem(SPECIALTY_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  // Inpatient note type (admission H&P or progress note), remembered the same way. An Events
  // note is a one-off, so it is not remembered: the next encounter starts on the usual type.
  const [noteFormat, setNoteFormatState] = useState(readDefaultNoteFormat);
  const setNoteFormat = useCallback((value) => {
    const format = normalizeNoteFormat(value);
    setNoteFormatState(format);
    if (format === NOTE_FORMATS.EVENTS) return;
    try {
      window.localStorage.setItem(NOTE_FORMAT_KEY, format);
    } catch {
      // ignore
    }
  }, []);
  // Event, Differential, Plan, Chart, with the events in place of a transcript.
  const eventsNote = isEventsNote(encounterSetting, noteFormat);

  const recordingHook = useEncounterRecording({ language: recordingLanguage });

  // Typing or pasting instead of recording. Held here, not on the Record step, so it is still
  // in effect when the clinician comes back to that step; a new encounter starts in voice again.
  const [textMode, setTextMode] = useState(false);

  // Clarifying questions are always on: they cost nothing on screen until there is one to ask,
  // so there is no switch for them.
  const clarifyingQuestionsEnabled = true;

  // Patient context imported from a patient-generated share code (QR / 7-digit).
  // Lives at session-level so the auto-analysis effect can re-fire when the
  // clinician scans a code mid-encounter, and so downstream steps can read it.
  // Declared before the step hooks because every one of them now consumes it.
  const [patientContext, setPatientContext] = useState(null);
  const clearPatientContext = useCallback(() => setPatientContext(null), []);

  // The single rendered form of the imported record that every step sends to the LLM.
  //
  // This is the fix for the ChartMind demo failure: previously only the differential
  // formatted the record, so medications and allergies never reached the diagnostic plan,
  // confirm, treatment, discharge or note prompts. Built once here and threaded into every
  // generator below, so a new step cannot quietly opt out of it.
  const patientContextPayload = useMemo(
    () => buildPatientContextPayload(patientContext),
    [patientContext],
  );

  // A session restored from Firestore keeps the patient's identity but not the clinical
  // record (see PERSIST_IMPORTED_PATIENT_RECORD). Surface that rather than letting every
  // step silently run record-blind, which is the original bug wearing a different hat.
  const patientRecordUnavailable = Boolean(
    patientContext?.patientUserId && !patientContext?.record,
  );

  // ============================================================================
  // PHONE MICROPHONE RELAY
  // ============================================================================
  //
  // Clinic desk mics are bad, so the clinician can record on their phone instead and have the
  // transcript stream in here. Only the transcript crosses; see CHARTMIND-PHONE-MIC.md.
  //
  // Mounted here rather than in RecordingStep so the pairing survives navigating away from the
  // Record step, exactly like the rest of this session.
  //
  // onTranscript is recordingHook.updateTranscript directly: the relay is just another writer
  // of the one string ChartMind already reads everywhere.
  //
  // onFinished has to reach finishCapture, which is defined further down because it depends on
  // runDdx. Rather than reorder half this file, it goes through a ref behind a stable callback —
  // the same shape as stableOnDataChanged above, and for the same reason: an inline arrow here
  // would be a new identity every render and would re-subscribe the relay on every keystroke.
  const finishCaptureRef = useRef(null);

  // ============================================================================
  // PHONE DICTATION INTO A CHART SECTION
  // ============================================================================
  //
  // Same wire, different destination. The clinician opens a note section's editor on the Chart
  // step, presses "Use phone", and the round they speak lands in that section instead of in the
  // encounter. See CHARTMIND-PHONE-DICTATION.md.
  //
  // ⚠️ THE TARGET IS ARMED HERE AND READ BY THE RELAY AT THE START OF EACH ROUND. The phone is
  // never told which section it is; it is told a title to print. It cannot choose one.
  const [phoneDictation, setPhoneDictation] = useState(null);
  // The relay reads this as a round begins, so it must not lag a render behind the state.
  const phoneDictationRef = useRef(null);
  const [phoneDictationText, setPhoneDictationText] = useState("");
  /**
   * The section's text as it stands RIGHT NOW, which is what the next round appends to.
   *
   * ⚠️ NOT `armed.seed`, AND THAT DISTINCTION IS THE WHOLE BUG IT FIXES. The seed is captured
   * once, when the editor hands the section to the phone, and never moves again. Tapping record
   * a second time without closing the editor therefore stitched round two onto the text as it
   * was BEFORE round one, and round one vanished. It looked intermittent because closing and
   * reopening the editor re-arms with a fresh seed, so the same gesture appended or replaced
   * depending on something the clinician had no reason to connect to it.
   */
  const phoneDictationTextRef = useRef("");

  const getRoundTarget = useCallback(() => {
    const armed = phoneDictationRef.current;
    return armed
      // No `seed` on the target on purpose: a round's base is read live from
      // phoneDictationTextRef, and carrying a stale copy here is what made a second round
      // overwrite the first.
      ? { kind: "section", key: armed.key, title: armed.title }
      : { kind: "encounter" };
  }, []);

  const getRoundBase = useCallback(
    (target) =>
      target?.kind === "section"
        ? phoneDictationTextRef.current || ""
        : recordingHook.getFinalTranscript(),
    [recordingHook],
  );

  const routeRoundTranscript = useCallback(
    (text, target) => {
      if (target?.kind === "section") {
        phoneDictationTextRef.current = text;
        setPhoneDictationText(text);
      } else recordingHook.updateTranscript(text);
    },
    [recordingHook],
  );

  /**
   * ⚠️ A SECTION ROUND MUST NEVER REACH finishCapture, which runs the differential.
   *
   * An addendum to one paragraph of a note is an edit, not a new encounter. Letting it through
   * would re-run the DDx, the treatment plan and the note itself off the back of "add that the
   * wound was clean and dry" — silently, and with no way for the clinician to tell it happened.
   */
  const routeRoundFinished = useCallback((text, { target } = {}) => {
    if (target?.kind === "section") {
      // The ref matters more than the state here: the next round reads it as it begins, which
      // can be before React has re-rendered.
      phoneDictationTextRef.current = text;
      setPhoneDictationText(text);
      return;
    }
    finishCaptureRef.current?.(text);
  }, []);

  const phoneRelay = usePhoneRecordingRelay({
    ownerUid: user?.uid,
    language: recordingLanguage,
    getRoundTarget,
    // The encounter as it stands when a round begins, or the section's existing text for a
    // dictation. The wire carries only the round, so this is what it gets stitched onto —
    // without it, tapping record again on the phone would replace the first half with the second.
    getBaseTranscript: getRoundBase,
    onTranscript: routeRoundTranscript,
    onFinished: routeRoundFinished,
  });

  // Pulled out as their own bindings so effects can depend on the two things that actually
  // change rather than on `phoneRelay`, which is a fresh object every render — depending on the
  // object would fire the questions push below on every keystroke of the encounter.
  const { isPhoneRecording, pushClarifyingQuestions } = phoneRelay;

  /**
   * True while the phone is recording an ENCOUNTER round, false while it is dictating a note
   * section.
   *
   * ⚠️ THE DISTINCTION IS LOAD-BEARING IN TWO PLACES and both fail silently without it: a
   * dictation round would otherwise put the phone-remote's Next behind "please stop the
   * recording first", and — worse — start the clarifying-questions poller against the encounter
   * transcript while the clinician is editing a finished note, billing LLM calls and pushing a
   * previous phase's questions onto the phone mid-sentence.
   */
  const phoneEncounterRecording = isPhoneRecording && !phoneDictation;

  /**
   * Arm the paired phone to dictate into one note section.
   *
   * `seed` is the text already in the section, which becomes the round base — so speaking ADDS
   * to the section rather than replacing it, exactly like desktop dictation.
   */
  const beginPhoneDictation = useCallback((key, title, seed = "") => {
    const armed = { key, title, seed };
    // Ref first and synchronously: the relay reads it as the round begins, which can be the very
    // next snapshot, and state would still be a render behind.
    phoneDictationRef.current = armed;
    setPhoneDictation(armed);
    phoneDictationTextRef.current = seed;
    setPhoneDictationText(seed);
  }, []);

  /**
   * Disarm it — the editor closed, saved or cancelled.
   *
   * ⚠️ STOP A ROUND THAT IS STILL RUNNING. Without this the phone keeps recording into a dialog
   * that is no longer on screen. The round's own target was captured at its start, so its text
   * still routes harmlessly to the section sink rather than into the encounter.
   */
  const cancelPhoneDictation = useCallback(() => {
    const wasArmed = phoneDictationRef.current;
    phoneDictationRef.current = null;
    setPhoneDictation(null);
    phoneDictationTextRef.current = "";
    setPhoneDictationText("");
    if (wasArmed && isPhoneRecording) phoneRelay.requestStop();
  }, [isPhoneRecording, phoneRelay]);

  /**
   * True while EITHER device is capturing.
   *
   * Read this, not recordingHook.isRecording, for any question of the form "is an encounter
   * being captured right now". recordingHook.isRecording answers the narrower question "does
   * THIS device own a microphone", which is what the mic button and the transcript field need.
   */
  const captureActive = recordingHook.isRecording || phoneEncounterRecording;

  // ⚠️ `isRecording` here means "text is still arriving, keep polling", NOT "this device owns a
  // microphone" — two different questions wearing one name. During a phone recording the desktop
  // owns no mic, so passing recordingHook.isRecording would silently switch clarifying questions
  // off for the entire phone flow. `captureActive` is defined just below the relay hook.
  const clarifyingQuestionsHook = useClarifyingQuestions({
    transcript: recordingHook.transcript,
    isRecording: recordingHook.isRecording || phoneEncounterRecording,
    enabled: clarifyingQuestionsEnabled,
    patientContext: patientContextPayload,
    // The encounter's language, so the questions come back in it rather than in whatever
    // language a garbled second of audio happened to be transcribed as.
    language: recordingLanguage,
  });
  // Inpatient encounters use the inpatient prompts, falling back to these when an environment
  // has not been seeded with them yet (scripts/seedInpatientPrompts.js).
  const diagnosisHook = useChartMind({
    onDataChanged: stableOnDataChanged,
    ...promptOptions("chartmind-diagnosis", encounterSetting, specialty),
  });
  // Pending Daily Plan items, read when the note comes back (kept in a ref because the Daily Plan
  // state is declared below this hook).
  const pendingDailyPlanRef = useRef([]);
  const finalizeInpatientNote = useCallback(
    (sections) => ensurePendingInNote(sections, pendingDailyPlanRef.current),
    [],
  );
  const noteTemplate = useMemo(
    () => noteTemplateFor(specialty, encounterSetting, noteFormat),
    [specialty, encounterSetting, noteFormat],
  );
  const chartHook = useChartGeneration({
    template: noteTemplate,
    documentationOutline: formatDocumentationOutline(noteTemplate, { setting: encounterSetting, noteFormat }),
    finalizeSections: inpatient ? finalizeInpatientNote : undefined,
    onDataChanged: stableOnDataChanged,
    ...promptOptions("chartmind-notes", encounterSetting, specialty),
    fallbackInstructions: inpatient && !eventsNote ? INPATIENT_FALLBACK_INSTRUCTIONS : undefined,
  });
  const diagnosticPlanHook = useDiagnosticPlan({ onDataChanged: stableOnDataChanged });
  const confirmDiagnosisHook = useConfirmDiagnosis({ onDataChanged: stableOnDataChanged });
  // The inpatient treatment prompt writes a whole-day Daily Plan, so an Events note uses the base
  // prompt with the inpatient focus, plus EVENTS_PLAN_FOCUS on the request.
  const treatmentPlanHook = useTreatmentPlan({
    onDataChanged: stableOnDataChanged,
    ...(eventsNote
      ? { focus: specialtyFocus("chartmind-treatment", specialty, encounterSetting) }
      : promptOptions("chartmind-treatment", encounterSetting, specialty)),
  });
  const dischargeHook = useChartMindDischarge({
    onDataChanged: stableOnDataChanged,
    ...promptOptions("chartmind-discharge", encounterSetting, specialty),
  });
  
  // Debounce transcript for manual text input (1.5s after typing stops)
  const [debouncedTranscript] = useDebounce(recordingHook.transcript, 1500);
  const reportingMetadata = useMemo(
    () => ({
      organizationId: organizationId || null,
      isSimulation: Boolean(isGlobalAdmin),
    }),
    [organizationId, isGlobalAdmin],
  );

  // ============================================================================
  // SESSION STATE
  // ============================================================================
  
  const [sessionId, setSessionId] = useState(initialSessionId);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!initialSessionId);
  const [sessionError, setSessionError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasLoadedSession, setHasLoadedSession] = useState(false); // Track if we've loaded the initial session
  const [lastSavedAt, setLastSavedAt] = useState(null); // Timestamp of last successful save
  
  // If we're loading a session on mount, start with hydrating flag true
  const [isInitialLoad] = useState(!!initialSessionId);
  
  // ============================================================================
  // AUTO-SAVE STATE
  // ============================================================================
  
  // Track last saved snapshot for change detection
  const lastSavedSnapshotRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const autoSaveSessionRef = useRef(null);
  const isSavingRef = useRef(false); // Prevent concurrent saves
  const isHydratingRef = useRef(isInitialLoad); // Start true if loading session on mount
  // Always-current ref for serializeSession — avoids stale closure inside loadSession's setTimeout
  const serializeSessionRef = useRef(null);

  // ============================================================================
  // LIFT LOCAL STATE (previously in ChartMindPage)
  // ============================================================================
  
  // Navigation state
  const [currentStep, setCurrentStep] = useState(STEPS.RECORD);
  const [visitedSteps, setVisitedSteps] = useState(new Set([STEPS.RECORD]));
  
  // Diagnosis state
  const [selectedDiagnoses, setSelectedDiagnoses] = useState([]);
  /** User-added diagnoses from "Add Diagnosis" — must live in session for save/hydrate */
  const [customDiagnoses, setCustomDiagnoses] = useState([]);
  const [disabledDiagnosisIds, setDisabledDiagnosisIds] = useState(new Set());
  /**
   * What the clinician thought it was BEFORE the differential was shown to them.
   *
   * Null means they have not answered yet, and that is what gates the Diagnosis step — see
   * ClinicianDiagnosisGate. { condition: "", undetermined: true } is a real recorded answer, not an
   * empty one, so it opens the gate like any other.
   */
  const [clinicianImpression, setClinicianImpression] = useState(null);
  
  // Diagnostic plan state
  const [disabledTestIds, setDisabledTestIds] = useState(new Set());
  const [testResults, setTestResults] = useState(new Map());
  
  // Confirmed diagnosis state
  const [selectedConfirmedDiagnoses, setSelectedConfirmedDiagnoses] = useState([]);
  
  // Treatment state
  const [customTreatments, setCustomTreatments] = useState([]);
  const [disabledTreatmentIds, setDisabledTreatmentIds] = useState(new Set());
  const [selectedTreatments, setSelectedTreatments] = useState(new Set());

  // Inpatient state: clinician-entered events, and a comment or reassuring mark per Daily Plan
  // item (keyed by treatment id).
  const [inpatientEvents, setInpatientEvents] = useState([]);
  const [dailyPlanComments, setDailyPlanComments] = useState({});
  const [dailyPlanReassuring, setDailyPlanReassuring] = useState({});

  // What the differential, plan and note are written from: the transcript, or on the Events note
  // the events, which stand in for it.
  const eventsSourceText = useMemo(
    () => (eventsNote ? buildEventsSourceText(inpatientEvents) : ""),
    [eventsNote, inpatientEvents],
  );
  const sourceText = eventsNote ? eventsSourceText : recordingHook.transcript;
  // The plan request adds a focus on the Events note (see the treatment hook above).
  const planSourceText = eventsNote && sourceText ? `${sourceText}\n\n${EVENTS_PLAN_FOCUS}` : sourceText;
  
  // UI behavior tracking refs (not serialized to DB)
  const userNavigatedRef = useRef(false);
  const analysisTriggeredRef = useRef(false);
  const generatedForRef = useRef(null);

  // ============================================================================
  // AUTO-ANALYSIS EFFECTS
  // ============================================================================

  // Patient context the differential currently on screen was generated with. Lets the
  // auto-analysis effect tell "we already have a differential" apart from "we already have
  // a differential FOR THIS PATIENT'S RECORD".
  const ddxPatientContextKeyRef = useRef("");
  // True while a pasted transcript is being clarified in the background. The differential waits
  // for the clarified text instead of running on the raw paste.
  const [pasteClarifying, setPasteClarifying] = useState(false);
  // The Visit type is part of the key too: it picks the differential's prompt, and it can still
  // change on the Record step after the background differential has run.
  const ddxInputsKey = `${patientContextPayloadKey(patientContextPayload)}|${encounterSetting}|${specialty}`;

  const runDdx = useCallback((transcript) => {
    ddxPatientContextKeyRef.current = ddxInputsKey;
    diagnosisHook.analyzeDDX(transcript, patientContextPayload);
  }, [diagnosisHook.analyzeDDX, patientContextPayload, ddxInputsKey]);

  // Auto-analyze on debounced manual text input (when not recording).
  //
  // The hasDiagnoses / analysisTriggered guards exist to stop the differential regenerating
  // on every render and on a loaded session. They also used to block the one case that must
  // get through: a clinician who scans the patient's code AFTER the differential has been
  // produced. That left the differential pinned to what was known before the import, which
  // is the bug this whole change is about, so a changed patient record overrides them.
  // analyzeDDX still dedups internally, so an unchanged record cannot cause a re-run.
  useEffect(() => {
    const ddxMissesPatientContext =
      diagnosisHook.hasDiagnoses && ddxPatientContextKeyRef.current !== ddxInputsKey;

    // ⚠️ captureActive, NOT recordingHook.isRecording. A phone recording streams the transcript
    // in while this device's mic is idle, so keying on the local mic alone let the FIRST ~50
    // characters to arrive fire the whole differential 1.5s later — on two sentences of an
    // encounter — after which hasDiagnoses pinned it there for good. The clinician finished a
    // twelve-minute encounter and found a differential built from "so what brings you in today,
    // my knee has been". finishCapture runs the real one when the recording actually ends.
    if (
      !eventsNote &&
      !captureActive &&
      !pasteClarifying &&
      // Settled text only. The debounce lags a moment behind the field, so right after a paste
      // is clarified it still holds the raw paste, which is not what should be analyzed.
      debouncedTranscript === recordingHook.transcript &&
      debouncedTranscript &&
      debouncedTranscript.length >= 50 &&
      !diagnosisHook.loading &&
      !diagnosisHook.error && // Don't auto-retry after a failed call — user must retry manually
      (!analysisTriggeredRef.current || ddxMissesPatientContext) &&
      (!diagnosisHook.hasDiagnoses || ddxMissesPatientContext)
    ) {
      runDdx(debouncedTranscript);
    }
  }, [eventsNote, debouncedTranscript, recordingHook.transcript, captureActive, pasteClarifying, diagnosisHook.loading, diagnosisHook.error, diagnosisHook.hasDiagnoses, ddxInputsKey, runDdx]);

  // Reset analysis trigger when transcript changes significantly
  useEffect(() => {
    analysisTriggeredRef.current = false;
  }, [recordingHook.transcript]);

  // ============================================================================
  // HANDLERS (lifted from ChartMindPage)
  // ============================================================================

  /**
   * Handle mic button click
   */
  /**
   * Everything that has to happen when a recording ends, wherever it was recorded.
   *
   * Extracted because a phone recording has to land in exactly the same state a desktop one
   * does. Without this, a phone encounter arrived on the Record step with no differential and
   * the flow the client asked for stalled at step one.
   */
  const finishCapture = useCallback((finalTranscript) => {
    userNavigatedRef.current = false;
    analysisTriggeredRef.current = true;
    if (!eventsNote && finalTranscript && finalTranscript.length >= 50) {
      runDdx(finalTranscript);
    }
  }, [eventsNote, runDdx]);

  useEffect(() => {
    finishCaptureRef.current = finishCapture;
  }, [finishCapture]);

  // Push clarifying questions down to the phone while it is the device recording.
  //
  // The DESKTOP writes them, because it is already polling for them. Running a second
  // useClarifyingQuestions on the phone would bill a second set of LLM calls for one encounter.
  // The phone is the screen actually in the clinician's hand, so it is the right place to show
  // them — and this is the only field the desktop writes on the relay, which keeps the
  // one-writer-per-field rule the security rules are built around.
  // ⚠️ ENCOUNTER ROUNDS ONLY. A phone dictating a note section is not taking a history, and the
  // last encounter's questions appearing over a dictation screen would be answering a question
  // nobody asked.
  useEffect(() => {
    if (!phoneEncounterRecording) return;
    pushClarifyingQuestions(clarifyingQuestionsHook.questions);
  }, [clarifyingQuestionsHook.questions, phoneEncounterRecording, pushClarifyingQuestions]);

  const handleMicClick = useCallback(async () => {
    if (recordingHook.isRecording) {
      await recordingHook.stopRecording();
      // Read from the ref directly — React state may not have flushed recognition.onend yet
      finishCapture(recordingHook.getFinalTranscript());
    } else {
      await recordingHook.startRecording();
    }
  }, [
    recordingHook.isRecording,
    recordingHook.stopRecording,
    recordingHook.startRecording,
    recordingHook.getFinalTranscript,
    finishCapture,
  ]);

  /**
   * Handle step change (manual navigation)
   */
  const handleStepChange = useCallback((step) => {
    userNavigatedRef.current = true;
    setCurrentStep(step);
    // Unlock Diagnosis when manually navigating to it
    if (step === STEPS.DIAGNOSIS && sourceText) {
      setVisitedSteps(prev => new Set([...prev, STEPS.DIAGNOSIS]));
    }
    // Auto-save on navigation (only if data changed)
    autoSaveSessionRef.current?.('handleStepChange');
  }, [sourceText]);

  /**
   * Handle diagnosis selection change
   */
  const handleSelectionChange = useCallback((newSelection) => {
    setSelectedDiagnoses(newSelection);
  }, []);

  /**
   * Add a custom diagnosis from the modal and select it so session snapshot updates (auto-save).
   */
  const handleAddCustomDiagnosis = useCallback((diagnosis) => {
    setCustomDiagnoses((prev) => [...prev, diagnosis]);
    setSelectedDiagnoses([diagnosis]);
  }, []);

  /**
   * Record the clinician's own diagnosis, given before they were shown the differential.
   *
   * It is stored twice on purpose. `clinicianImpression` is the answer as given, with its
   * timestamp, and is what the gate checks — nothing the clinician does later can walk it back
   * into "I never said that". The custom-diagnosis copy is the same answer as a usable card:
   * it selects, hides, saves and hydrates through the paths every other diagnosis already uses,
   * which is why this needed no new plumbing downstream.
   *
   * ⚠️ IT IS NOT SELECTED ON THEIR BEHALF. Answering the gate is not the same as deciding —
   * the client was explicit that a diagnosis they gave before seeing the differential is a
   * candidate, not something to hand a "Final Diagnosis" chip and a green light on Continue.
   * The clinician has to click a card, theirs or the model's, before anything counts as chosen.
   * An edit made *while their card is already selected* is the one exception: that is fixing
   * wording on a decision already made, not making a new one, so the selection follows it.
   */
  const handleRecordClinicianImpression = useCallback((impression) => {
    const condition = (impression?.condition || "").trim();
    const reasoning = (impression?.reasoning || "").trim();
    const now = formatISO(new Date());

    setClinicianImpression((prev) => {
      // ⚠️ THE FIRST ANSWER IS NOT OVERWRITTEN BY A LATER EDIT. Revising a working diagnosis
      // after reading the differential is ordinary practice and the encounter has to carry the
      // revision — but "what did you think before you saw the list" is the one question here
      // that can only be answered once, so the original stays on the record beside it.
      const firstCondition = prev
        ? prev.firstCondition || prev.condition || ""
        : condition;
      return {
        condition,
        reasoning,
        undetermined: Boolean(impression?.undetermined) || !condition,
        firstCondition,
        recordedAt: prev?.recordedAt || now,
        ...(prev && { revisedAt: now }),
      };
    });

    const diagnosis = condition
      ? {
          condition,
          ...(reasoning && { rationale: reasoning }),
          isCustom: true,
          isClinicianImpression: true,
        }
      : null;

    // An edit replaces the card rather than adding a second one, and takes the selection with
    // it — leaving the old wording selected would send the superseded diagnosis to the plan.
    setCustomDiagnoses((prev) => {
      const others = prev.filter((dx) => !dx.isClinicianImpression);
      return diagnosis ? [...others, diagnosis] : others;
    });
    setSelectedDiagnoses((prev) => {
      const wasOwnSelected = prev.some((dx) => dx.isClinicianImpression);
      if (!wasOwnSelected) return prev;
      return diagnosis ? [diagnosis] : [];
    });
  }, []);

  /**
   * When the model listed the clinician's diagnosis too, its entry becomes the one the
   * encounter carries.
   *
   * ⚠️ WHICH OBJECT IS SELECTED DECIDES WHAT THE REST OF THE ENCOUNTER IS BUILT FROM, and the
   * two copies are not equivalent. The model's entry has the canonical spelling and the
   * rationale that the treatment, discharge and note prompts read; the clinician's has their own
   * wording, typo and all. Leaving theirs selected sent "Influenzaa" to the chart note and threw
   * away the reasoning behind the ranking.
   *
   * ⚠️ NOTHING IS LOST BY THIS. Their wording and their findings live in clinicianImpression,
   * which is the record of what they said and is never rewritten here — the Diagnosis step shows
   * both sets of reasoning on the one card. This only removes the duplicate CARD.
   *
   * ⚠️ THIS DOES NOT SELECT ANYTHING THAT WASN'T ALREADY SELECTED. Swapping cards when nothing
   * is chosen yet would be the same auto-select the clinician's own answer no longer gets — the
   * differential landing is not a decision either. Only a card the clinician had already picked
   * follows the swap, so what is selected does not silently change out from under them.
   *
   * It runs as an effect rather than inside the handler because the differential often lands
   * after the answer: a clinician who types quickly answers while it is still generating.
   */
  useEffect(() => {
    const condition = clinicianImpression?.condition;
    if (!condition) return;

    const ddxEntries = [
      ...(diagnosisHook.ddxData?.primary_diagnoses || []),
      ...(diagnosisHook.ddxData?.alternative_diagnoses || []),
    ];
    const match = ddxEntries.find((dx) => isSameCondition(dx.condition, condition));
    if (!match) return;

    setCustomDiagnoses((prev) =>
      prev.some((dx) => dx.isClinicianImpression)
        ? prev.filter((dx) => !dx.isClinicianImpression)
        : prev,
    );
    setSelectedDiagnoses((prev) =>
      prev.some((dx) => dx.isClinicianImpression) ? [match] : prev,
    );
  }, [diagnosisHook.ddxData, clinicianImpression]);

  /**
   * Handle confirmed diagnosis selection change
   */
  const handleConfirmedSelectionChange = useCallback((newSelection) => {
    setSelectedConfirmedDiagnoses(newSelection);
  }, []);

  /**
   * Toggle diagnosis disabled state
   */
  const handleToggleDiagnosisDisabled = useCallback((diagnosisId) => {
    setDisabledDiagnosisIds((prev) => {
      const next = new Set(prev);
      if (next.has(diagnosisId)) {
        next.delete(diagnosisId);
      } else {
        next.add(diagnosisId);
      }
      return next;
    });
  }, []);

  /**
   * Toggle diagnostic test disabled state
   */
  const handleToggleTestDisabled = useCallback((testId) => {
    setDisabledTestIds((prev) => {
      const next = new Set(prev);
      if (next.has(testId)) {
        next.delete(testId);
      } else {
        next.add(testId);
      }
      return next;
    });
  }, []);

  /**
   * Update test result
   */
  const handleUpdateTestResult = useCallback((testId, result) => {
    setTestResults((prev) => {
      const next = new Map(prev);
      if (result === null || result === undefined) {
        next.delete(testId);
      } else {
        next.set(testId, result);
      }
      return next;
    });
  }, []);

  /**
   * Add a custom treatment from the modal and auto-select it.
   */
  const handleAddCustomTreatment = useCallback((treatment) => {
    setCustomTreatments((prev) => [...prev, treatment]);
    setSelectedTreatments((prev) => {
      const next = new Set(prev);
      next.add(treatment.id);
      return next;
    });
  }, []);

  /**
   * Toggle treatment disabled state
   */
  const handleToggleTreatmentDisabled = useCallback((treatmentId) => {
    setDisabledTreatmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(treatmentId)) {
        next.delete(treatmentId);
      } else {
        next.add(treatmentId);
        // Auto-deselect when disabling
        setSelectedTreatments((prevSelected) => {
          const nextSelected = new Set(prevSelected);
          nextSelected.delete(treatmentId);
          return nextSelected;
        });
      }
      return next;
    });
  }, []);

  /**
   * Toggle treatment selected state
   */
  const handleToggleTreatmentSelected = useCallback((treatmentId) => {
    setSelectedTreatments((prev) => {
      const next = new Set(prev);
      if (next.has(treatmentId)) {
        next.delete(treatmentId);
      } else {
        next.add(treatmentId);
      }
      return next;
    });
  }, []);

  const handleAddInpatientEvent = useCallback((name, comment = "", occurredAt = "") => {
    if (!String(name || "").trim()) return;
    setInpatientEvents((prev) => [...prev, createInpatientEvent(name, comment, occurredAt)]);
  }, []);

  const handleUpdateInpatientEvent = useCallback((eventId, { name, comment, occurredAt }) => {
    setInpatientEvents((prev) =>
      prev.map((event) =>
        event.id === eventId
          ? {
              ...event,
              ...(name ? { name } : {}),
              ...(comment !== undefined ? { comment } : {}),
              ...(occurredAt !== undefined
                ? { occurredAt: parseEventTime(occurredAt) ? occurredAt : "" }
                : {}),
            }
          : event,
      ),
    );
  }, []);

  const handleRemoveInpatientEvent = useCallback((eventId) => {
    setInpatientEvents((prev) => prev.filter((event) => event.id !== eventId));
  }, []);

  const handleDailyPlanCommentChange = useCallback((itemId, comment) => {
    setDailyPlanComments((prev) => {
      const next = { ...prev };
      if (String(comment || "").trim()) next[itemId] = comment;
      else delete next[itemId];
      return next;
    });
  }, []);

  const handleSetDailyPlanReassuring = useCallback((itemId, reassuring) => {
    setDailyPlanReassuring((prev) => {
      const next = { ...prev };
      if (reassuring) next[itemId] = true;
      else delete next[itemId];
      return next;
    });
  }, []);

  /**
   * What an inpatient note is written from beyond the transcript and diagnoses. Empty for
   * outpatient, which leaves the outpatient note request exactly as it was.
   */
  const inpatientNoteContext = useMemo(() => {
    if (!inpatient) return "";
    const checkedItems = [
      ...(treatmentPlanHook.planData?.treatments || []),
      ...customTreatments,
    ].filter((t) => selectedTreatments.has(t.id) && !disabledTreatmentIds.has(t.id));
    // The Events note has no Daily Plan, so nothing is pending.
    if (eventsNote) {
      pendingDailyPlanRef.current = [];
      return buildEventsNoteContext({ events: inpatientEvents, planItems: checkedItems });
    }
    pendingDailyPlanRef.current = pendingDailyPlanNames(checkedItems, {
      comments: dailyPlanComments,
      reassuring: dailyPlanReassuring,
    });
    return buildInpatientNoteContext({
      checkedItems,
      comments: dailyPlanComments,
      reassuring: dailyPlanReassuring,
      events: inpatientEvents,
      noteFormat,
    });
  }, [
    inpatient,
    eventsNote,
    noteFormat,
    treatmentPlanHook.planData,
    customTreatments,
    selectedTreatments,
    disabledTreatmentIds,
    dailyPlanComments,
    dailyPlanReassuring,
    inpatientEvents,
  ]);
  const noteEncounterDetails = inpatient ? { inpatient: inpatientNoteContext } : null;

  /**
   * Create stable identifier for generation request
   */
  const getGenerationKey = useCallback((transcript, diagnoses) => {
    // Covers the WHOLE transcript and EVERY selected diagnosis. The old key was
    // `transcript.slice(0, 100)` plus the first diagnosis, so the common "go back and add more
    // detail" edit landed past character 100, left the key unchanged, and the note was silently
    // treated as current. djb2 keeps the key short without dropping any input.
    // The patient context is part of the key so importing a record after a note exists both
    // marks the note stale in the banner and forces a regenerate rather than serving the
    // note that was written without the record.
    const conditions = (diagnoses || [])
      .map((d) => d?.condition || d?.name || '')
      .sort()
      .join(',');
    // Inpatient adds the Daily Plan, Events and note type, so editing any of them marks the note
    // stale.
    const inpatientKey = inpatient ? `|${djb2(inpatientNoteContext)}|${noteFormat}` : '';
    // A different specialty writes a different note.
    const specialtyKey = `|${specialty}`;
    return `${djb2(transcript || '')}:${(transcript || '').length}|${djb2(conditions)}|${patientContextPayloadKey(patientContextPayload)}${inpatientKey}${specialtyKey}`;
  }, [patientContextPayload, inpatient, inpatientNoteContext, noteFormat, specialty]);

  /**
   * Prefetch note generation (called after debounce when diagnosis selected)
   * DISABLED: This was causing premature note generation before discharge step
   */
  const handlePrefetch = useCallback(async (diagnoses) => {
    // Prefetch disabled - note will only generate when user clicks "Generate Note" button
    // if (diagnoses.length > 0 && recordingHook.transcript) {
    //   const genKey = getGenerationKey(recordingHook.transcript, diagnoses);
    //   generatedForRef.current = genKey;
    //   await chartHook.generateNote(recordingHook.transcript, diagnoses);
    // }
  }, [currentStep]);

  /**
   * Continue to Diagnosis step
   */
  const handleContinueToDiagnosis = useCallback(() => {
    setCurrentStep(STEPS.DIAGNOSIS);
    setVisitedSteps(prev => new Set([...prev, eventsNote ? STEPS.EVENTS : STEPS.RECORD, STEPS.DIAGNOSIS]));
    autoSaveSessionRef.current?.('handleContinueToDiagnosis');
  }, [eventsNote]);

  /**
   * The Events note's differential. There is no transcript for the auto-analysis to settle on, so
   * it runs whenever the Differential step is open, however it was reached (Next or the stepper),
   * and again once a run in flight finishes if the events changed meanwhile. analyzeDDX skips the
   * call when the events have not changed since the last one. Not after a failure: that is the
   * step's Retry, the same as the transcript's auto-analysis.
   *
   * eventsDdxSourceRef holds the events the differential on screen was made from, so a reloaded
   * session keeps its saved differential instead of paying for a new one.
   */
  const eventsDdxSourceRef = useRef("");
  useEffect(() => {
    if (!eventsNote || currentStep !== STEPS.DIAGNOSIS || !eventsSourceText) return;
    if (diagnosisHook.loading || diagnosisHook.error) return;
    if (diagnosisHook.hasDiagnoses && eventsDdxSourceRef.current === eventsSourceText) return;
    eventsDdxSourceRef.current = eventsSourceText;
    runDdx(eventsSourceText);
  }, [
    eventsNote,
    currentStep,
    eventsSourceText,
    diagnosisHook.loading,
    diagnosisHook.error,
    diagnosisHook.hasDiagnoses,
    runDdx,
  ]);

  /**
   * Continue to Diagnostic Plan step
   */
  // ============================================================================
  // PEARLS (folded in from ChartMind V2)
  // ============================================================================

  // Teaching pearls about the first chosen diagnosis. `pearlsUnavailable` outlives a cleared
  // session on purpose: it means the prompt is not set up on this environment, not anything
  // about the encounter.
  const [pearls, setPearls] = useState([]);
  const [pearlsSubject, setPearlsSubject] = useState("");
  const [pearlsLoading, setPearlsLoading] = useState(false);
  const [pearlsError, setPearlsError] = useState("");
  const [pearlsUnavailable, setPearlsUnavailable] = useState(false);
  // Bumped by every run and by clearSession, so a late answer for an older diagnosis or a
  // previous encounter is dropped instead of landing in this one.
  const pearlsRequestRef = useRef(0);

  const runPearls = useCallback(
    async (subject) => {
      if (!subject) return;
      const requestId = ++pearlsRequestRef.current;
      setPearlsLoading(true);
      setPearlsError("");
      try {
        const result = await generatePearls({
          subject,
          organizationId,
          patientContext: patientContextPayload,
          ...promptOptions(PEARLS_PROMPT_ID, encounterSetting, specialty),
        });
        if (requestId !== pearlsRequestRef.current) return;
        setPearls(result);
        setPearlsSubject(subject);
        onDataChangedRef.current?.();
      } catch (err) {
        if (requestId !== pearlsRequestRef.current) return;
        // The page moves a clinician already on the step on through its normal Next, so the
        // step after it generates as usual.
        if (isMissingPromptError(err)) setPearlsUnavailable(true);
        else setPearlsError(err?.message || "Could not load teaching pearls.");
      } finally {
        if (requestId === pearlsRequestRef.current) setPearlsLoading(false);
      }
    },
    [organizationId, patientContextPayload, encounterSetting, specialty],
  );

  const pearlsTarget = selectedDiagnoses[0]?.condition || selectedDiagnoses[0]?.name || "";

  // Generate on arrival, however the step was reached (Next, the stepper, Back), and again when
  // the chosen diagnosis has changed since the pearls on screen were written.
  useEffect(() => {
    if (currentStep !== STEPS.PEARLS || pearlsUnavailable || pearlsLoading || pearlsError) return;
    if (pearlsTarget && pearlsTarget !== pearlsSubject) void runPearls(pearlsTarget);
  }, [currentStep, pearlsTarget, pearlsSubject, pearlsUnavailable, pearlsLoading, pearlsError, runPearls]);

  const handleContinueToPearls = useCallback(() => {
    setCurrentStep(STEPS.PEARLS);
    setVisitedSteps(prev => new Set([...prev, STEPS.DIAGNOSIS, STEPS.PEARLS]));
    autoSaveSessionRef.current?.('handleContinueToPearls');
  }, []);

  const handleContinueToPlan = useCallback(() => {
    setCurrentStep(STEPS.DIAGNOSTIC_PLAN);
    setVisitedSteps(prev => new Set([...prev, STEPS.DIAGNOSIS, STEPS.DIAGNOSTIC_PLAN]));
    // Generate diagnostic plan based on transcript and selected diagnoses
    if (recordingHook.transcript && selectedDiagnoses.length > 0) {
      diagnosticPlanHook.generatePlan(
        recordingHook.transcript,
        selectedDiagnoses,
        patientContextPayload,
      );
    }
    autoSaveSessionRef.current?.('handleContinueToPlan');
  }, [recordingHook.transcript, selectedDiagnoses, diagnosticPlanHook.generatePlan, patientContextPayload]);

  /**
   * Continue to Confirm Diagnosis step
   */
  const handleContinueToConfirm = useCallback(() => {
    setCurrentStep(STEPS.CONFIRM_DIAGNOSIS);
    setVisitedSteps(prev => new Set([...prev, STEPS.DIAGNOSTIC_PLAN, STEPS.CONFIRM_DIAGNOSIS]));
    // Generate confirmed diagnoses based on test results
    // IMPORTANT: Pass the ORIGINAL DDX data (not just selected diagnoses) as the seed
    if (recordingHook.transcript && diagnosisHook.ddxData) {
      confirmDiagnosisHook.generateConfirmedDiagnoses(
        recordingHook.transcript,
        testResults,
        diagnosisHook.ddxData,  // Original DDX is the seed
        disabledDiagnosisIds,   // Pass disabled diagnosis IDs to LLM
        patientContextPayload
      );
    }
    autoSaveSessionRef.current?.('handleContinueToConfirm');
  }, [
    recordingHook.transcript,
    diagnosisHook.ddxData,
    testResults,
    disabledDiagnosisIds,
    confirmDiagnosisHook.generateConfirmedDiagnoses,
    patientContextPayload
  ]);

  /**
   * Continue to Treatment step
   */
  const handleContinueToTreatment = useCallback(() => {
    setCurrentStep(STEPS.TREATMENT);
    setVisitedSteps(prev => new Set([...prev, STEPS.CONFIRM_DIAGNOSIS, STEPS.TREATMENT]));
    // Generate treatment plan - use confirmed diagnoses if available, otherwise use preliminary
    const diagnosesForTreatment = selectedConfirmedDiagnoses.length > 0 
      ? selectedConfirmedDiagnoses 
      : selectedDiagnoses;
    
    if (sourceText && diagnosesForTreatment.length > 0) {
      treatmentPlanHook.generatePlan(
        planSourceText,
        diagnosesForTreatment,
        diagnosticPlanHook.planData,
        patientContextPayload
      );
    }
    autoSaveSessionRef.current?.('handleContinueToTreatment');
  }, [
    sourceText,
    planSourceText,
    selectedDiagnoses,
    selectedConfirmedDiagnoses,
    diagnosticPlanHook.planData,
    treatmentPlanHook.generatePlan,
    patientContextPayload
  ]);

  /**
   * The Events note's first step is Event, in place of Record: choosing the note type goes
   * straight there, and so does a new or reloaded Events encounter. The Event step has the setup
   * rows too, so choosing another note type or visit type there comes back to Record.
   */
  useEffect(() => {
    if (eventsNote && currentStep === STEPS.RECORD) {
      setCurrentStep(STEPS.EVENTS);
      setVisitedSteps(prev => new Set([...prev, STEPS.EVENTS]));
    } else if (!eventsNote && currentStep === STEPS.EVENTS) {
      setCurrentStep(STEPS.RECORD);
    }
  }, [eventsNote, currentStep]);


  /**
   * Continue to Discharge step
   */
  const handleContinueToDischarge = useCallback(() => {
    setCurrentStep(STEPS.DISCHARGE);
    setVisitedSteps(prev => new Set([...prev, STEPS.TREATMENT, STEPS.DISCHARGE]));
    autoSaveSessionRef.current?.('handleContinueToDischarge');
  }, []);

  /**
   * Generate the note unless one already exists for this transcript + diagnosis combo.
   * Shared by the normal Discharge -> Chart hand-off and the Record -> Chart skip.
   */
  const generateNoteIfStale = useCallback(async () => {
    const genKey = getGenerationKey(sourceText, selectedDiagnoses);
    const alreadyGenerated =
      chartHook.hasNote &&
      chartHook.noteSections &&
      Object.keys(chartHook.noteSections).length > 0 &&
      generatedForRef.current === genKey;

    if (!alreadyGenerated) {
      generatedForRef.current = genKey;
      await chartHook.generateNote(
        sourceText,
        selectedDiagnoses,
        patientContextPayload,
        noteEncounterDetails,
      );
    }
  }, [
    sourceText,
    selectedDiagnoses,
    chartHook.generateNote,
    chartHook.hasNote,
    chartHook.noteSections,
    getGenerationKey,
    patientContextPayload,
    inpatientNoteContext,
  ]);

  // Key describing the inputs the note SHOULD reflect right now. Computed during render from the
  // same function the generator uses, so the staleness banner can never disagree with the caching
  // decision in generateNoteIfStale.
  const currentGenerationKey = getGenerationKey(
    sourceText || '',
    selectedDiagnoses,
  );

  /**
   * True when a note is on screen but the transcript or diagnoses have changed since it was
   * generated — the "go back, add detail, return to Chart" case. Drives an inline prompt to
   * regenerate rather than silently showing an out-of-date note.
   */
  const isNoteStale = Boolean(
    chartHook.hasNote &&
      generatedForRef.current &&
      generatedForRef.current !== currentGenerationKey,
  );

  /**
   * Generate on arrival at Chart when there is nothing to show yet.
   *
   * The stepper navigates with setCurrentStep directly, bypassing the navigation handlers, so
   * without this a user who jumps to Chart lands on an empty step and has to press a button for
   * something we already have every input to produce. Keyed by generation key so a failed or
   * empty result cannot loop.
   */
  const autoGeneratedForRef = useRef(null);
  useEffect(() => {
    if (currentStep !== STEPS.CHART) return;
    if (chartHook.loading || chartHook.error || chartHook.hasNote) return;
    // The Events note's source is built from at least one event, so any event is enough.
    if (!sourceText || (!eventsNote && sourceText.trim().length < 50)) return;
    if (autoGeneratedForRef.current === currentGenerationKey) return;
    autoGeneratedForRef.current = currentGenerationKey;
    void generateNoteIfStale();
  }, [
    currentStep,
    chartHook.loading,
    chartHook.error,
    chartHook.hasNote,
    sourceText,
    eventsNote,
    currentGenerationKey,
    generateNoteIfStale,
  ]);

  /**
   * Continue to Chart step
   */
  const handleContinueToChart = useCallback(async () => {
    setCurrentStep(STEPS.CHART);
    setVisitedSteps(prev => new Set([...prev, STEPS.DISCHARGE, STEPS.CHART]));
    await generateNoteIfStale();
    autoSaveSessionRef.current?.('handleContinueToChart');
  }, [generateNoteIfStale]);

  /**
   * Skip Record -> Chart, generating the note from the transcript alone.
   *
   * Deliberately NOT handleContinueToChart: that marks Discharge visited as well, which would
   * unlock a step the user just chose to skip and let it auto-generate against no diagnoses.
   * Only Chart is marked visited here, so the stepper still reflects what was actually done.
   */
  const handleSkipToChart = useCallback(async () => {
    setCurrentStep(STEPS.CHART);
    setVisitedSteps(prev => new Set([...prev, STEPS.CHART]));
    await generateNoteIfStale();
    autoSaveSessionRef.current?.('handleSkipToChart');
  }, [generateNoteIfStale]);

  /**
   * Copy note to clipboard
   */
  const handleCopyToClipboard = useCallback(async () => {
    const text = chartHook.formatForClipboard();
    await navigator.clipboard.writeText(text);
  }, [chartHook.formatForClipboard]);

  /**
   * Export clinical note to PDF
   */
  const handleExportToPdf = useCallback(async () => {
    if (!chartHook.noteSections || !chartHook.template) {
      throw new Error("No clinical note available to export.");
    }

    const { fileName, blob } = await exportClinicalNotePdf({
      templateName: chartHook.template.name,
      sections: chartHook.template.sections.map(({ key, title }) => ({
        key,
        title,
      })),
      noteSections: chartHook.noteSections,
    });

    downloadBlob(blob, fileName);
  }, [chartHook.noteSections, chartHook.template]);

  /**
   * Regenerate note
   */
  const handleRegenerate = useCallback(async () => {
    generatedForRef.current = null;
    const genKey = getGenerationKey(sourceText, selectedDiagnoses);
    generatedForRef.current = genKey;
    await chartHook.generateNote(
      sourceText,
      selectedDiagnoses,
      patientContextPayload,
      noteEncounterDetails,
    );
  }, [sourceText, selectedDiagnoses, chartHook.generateNote, getGenerationKey, patientContextPayload, inpatientNoteContext]);

  /**
   * Handle back navigation
   */
  const handleBack = useCallback(() => {
    if (currentStep === STEPS.DIAGNOSIS) {
      setCurrentStep(eventsNote ? STEPS.EVENTS : STEPS.RECORD);
    } else if (currentStep === STEPS.PEARLS) {
      setCurrentStep(STEPS.DIAGNOSIS);
    } else if (currentStep === STEPS.DIAGNOSTIC_PLAN) {
      setCurrentStep(pearlsUnavailable ? STEPS.DIAGNOSIS : STEPS.PEARLS);
    } else if (currentStep === STEPS.CONFIRM_DIAGNOSIS) {
      setCurrentStep(STEPS.DIAGNOSTIC_PLAN);
    } else if (currentStep === STEPS.TREATMENT) {
      setCurrentStep(STEPS.CONFIRM_DIAGNOSIS);
    } else if (currentStep === STEPS.DISCHARGE) {
      setCurrentStep(STEPS.TREATMENT);
    } else if (currentStep === STEPS.CHART) {
      setCurrentStep(eventsNote ? STEPS.TREATMENT : STEPS.DISCHARGE);
    }
    autoSaveSessionRef.current?.('handleBack');
  }, [currentStep, eventsNote, pearlsUnavailable]);

  // ============================================================================
  // SERIALIZATION (Phase 2) - MUST BE BEFORE AUTO-SAVE
  // ============================================================================

  /**
   * Helper: Check if a value is "empty" (null, undefined, empty string, empty array, empty object)
   */
  const isEmpty = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (Array.isArray(value) && value.length === 0) return true;
    if (typeof value === 'object' && Object.keys(value).length === 0) return true;
    return false;
  };

  /**
   * Helper: Remove empty values from an object recursively
   */
  const removeEmptyValues = (obj) => {
    if (Array.isArray(obj)) {
      const filtered = obj.filter(item => !isEmpty(item));
      return filtered.length > 0 ? filtered : undefined;
    }
    
    if (typeof obj === 'object' && obj !== null) {
      const cleaned = {};
      for (const [key, value] of Object.entries(obj)) {
        const cleanedValue = removeEmptyValues(value);
        if (!isEmpty(cleanedValue)) {
          cleaned[key] = cleanedValue;
        }
      }
      return Object.keys(cleaned).length > 0 ? cleaned : undefined;
    }
    
    return isEmpty(obj) ? undefined : obj;
  };

  /**
   * Serialize current session state to JSON-safe object for Firestore
   * Optimizations:
   * - Excludes: empty sections, navigation, template, version
   * - Excludes: timestamp (added by Firestore via serverTimestamp)
   * - Stores diagnosis references (condition names) instead of full objects
   * - Minimal data for efficient storage
   */
  const serializeSession = useCallback(() => {
    // Source documents (clinical references + embedded RAG docs) backing a
    // step's LLM response — compacted to display fields so RAG chunk text
    // never lands in Firestore. Omitted entirely when empty.
    const sourceFields = (hook) => {
      const clinicalReferences = compactSourceList(hook.clinicalReferences);
      const embeddedDocuments = compactSourceList(hook.embeddedDocuments);
      return {
        ...(clinicalReferences.length > 0 && { clinicalReferences }),
        ...(embeddedDocuments.length > 0 && { embeddedDocuments }),
      };
    };

    const raw = {
      // Navigation state (current step)
      navigation: {
        currentStep,
      },

      pearls: pearls.length ? { pearls, subject: pearlsSubject } : undefined,

      specialty,

      // Only inpatient is stored; a session without it is outpatient.
      encounterSetting: inpatient ? encounterSetting : undefined,
      inpatient: inpatient ? {
        noteFormat,
        events: inpatientEvents,
        dailyPlanComments,
        dailyPlanReassuring,
      } : undefined,
      
      // Recording state (only if transcript exists)
      recording: recordingHook.transcript && recordingHook.transcript.trim() ? {
        transcript: recordingHook.transcript,
      } : undefined,
      
      // Diagnosis state (only if has data)
      // Store ddxData (full AI response) + selected diagnosis names (not full objects)
      diagnosis: (diagnosisHook.ddxData || (selectedDiagnoses && selectedDiagnoses.length > 0) || disabledDiagnosisIds.size > 0 || customDiagnoses.length > 0 || clinicianImpression) ? {
        ...(diagnosisHook.ddxData && { ddxData: diagnosisHook.ddxData }),
        ...(customDiagnoses.length > 0 && { customDiagnoses }),
        // The pre-differential answer. Saved so reopening a chart does not re-ask a question
        // that can only honestly be answered once.
        ...(clinicianImpression && { clinicianImpression }),
        // Store only condition names, not full objects (reduces duplication)
        ...(selectedDiagnoses && selectedDiagnoses.length > 0 && { 
          selectedDiagnosisNames: selectedDiagnoses.map(d => d.condition || d.name)
        }),
        ...(disabledDiagnosisIds.size > 0 && { disabledDiagnosisIds: Array.from(disabledDiagnosisIds) }),
        ...sourceFields(diagnosisHook),
      } : undefined,
      
      // Diagnostic plan state (only if has data)
      diagnosticPlan: (diagnosticPlanHook.planData || disabledTestIds.size > 0 || testResults.size > 0) ? {
        ...(diagnosticPlanHook.planData && { planData: diagnosticPlanHook.planData }),
        ...(disabledTestIds.size > 0 && { disabledTestIds: Array.from(disabledTestIds) }),
        // Convert Map to plain object for Firestore (doesn't support nested arrays)
        ...(testResults.size > 0 && { testResults: Object.fromEntries(testResults) }),
      } : undefined,
      
      // Confirmed diagnosis state (only if has data)
      confirmDiagnosis: (confirmDiagnosisHook.confirmedData || (selectedConfirmedDiagnoses && selectedConfirmedDiagnoses.length > 0)) ? {
          ...(confirmDiagnosisHook.confirmedData && { confirmedData: confirmDiagnosisHook.confirmedData }),
          // Store only condition names, not full objects (reduces duplication)
          ...(selectedConfirmedDiagnoses && selectedConfirmedDiagnoses.length > 0 && { 
            selectedConfirmedDiagnosisNames: selectedConfirmedDiagnoses.map(d => d.condition || d.name)
          }),
        } : undefined,
      
      // Treatment state (only if has data)
      treatment: (treatmentPlanHook.planData || disabledTreatmentIds.size > 0 || selectedTreatments.size > 0 || customTreatments.length > 0) ? {
        ...(treatmentPlanHook.planData && { planData: treatmentPlanHook.planData }),
        ...(customTreatments.length > 0 && { customTreatments }),
        ...(disabledTreatmentIds.size > 0 && { disabledTreatmentIds: Array.from(disabledTreatmentIds) }),
        ...(selectedTreatments.size > 0 && { selectedTreatments: Array.from(selectedTreatments) }),
        ...sourceFields(treatmentPlanHook),
      } : undefined,
      
      // Discharge state (only if has instructions or patientName - date only included if other data exists)
      discharge: (() => {
        const hasInstructions = dischargeHook.instructions && dischargeHook.instructions.length > 0;
        const hasPatientName = dischargeHook.patientName && dischargeHook.patientName.trim();
        
        // Only create discharge object if there are instructions or patientName
        if (!hasInstructions && !hasPatientName) {
          return undefined;
        }
        
        // Build discharge object with only non-empty fields
        const discharge = {};
        if (hasInstructions) {
          discharge.instructions = dischargeHook.instructions;
          discharge.includedInstructionIds = Array.from(dischargeHook.includedInstructionIds);
        }
        if (hasPatientName) {
          discharge.patientName = dischargeHook.patientName;
        }
        // Only include date if there are instructions or patientName (not just default date alone)
        if ((hasInstructions || hasPatientName) && dischargeHook.date) {
          discharge.date = dischargeHook.date;
        }
        if (dischargeHook.hasBeenGenerated) {
          discharge.hasBeenGenerated = dischargeHook.hasBeenGenerated;
        }
        Object.assign(discharge, sourceFields(dischargeHook));

        return Object.keys(discharge).length > 0 ? discharge : undefined;
      })(),
      
      // Imported patient context (when a clinician imports a shared record).
      //
      // Identity + consent always: that is what restores the "Send Discharge Instructions"
      // path on reload. The clinical record itself is gated on
      // PERSIST_IMPORTED_PATIENT_RECORD (see the constant at the top of this file) because
      // storing it outlives the share token's 60-minute consent window. Without it, a
      // reloaded session has a patient but no record, which is what
      // patientContext.recordUnavailable warns the clinician about.
      patientContext: (patientContext && patientContext.patientUserId) ? {
        patientUserId: patientContext.patientUserId,
        ...(patientContext.patientName && { patientName: patientContext.patientName }),
        ...(patientContext.patientDob && { patientDob: patientContext.patientDob }),
        ...(Array.isArray(patientContext.allowedSections) && patientContext.allowedSections.length > 0 && {
          allowedSections: patientContext.allowedSections,
        }),
        ...(PERSIST_IMPORTED_PATIENT_RECORD && patientContext.record && {
          record: patientContext.record,
        }),
        ...(PERSIST_IMPORTED_PATIENT_RECORD && patientContext.careStartResult && {
          careStartResult: patientContext.careStartResult,
        }),
        // Gated with the other two, not forgotten: the VisitStart check-in is the
        // patient's self-report and belongs to the same consent window. Left out,
        // flipping the flag above would restore the record and the CareStart result
        // after a reload and silently drop the intake.
        ...(PERSIST_IMPORTED_PATIENT_RECORD && patientContext.visitStartIntake && {
          visitStartIntake: patientContext.visitStartIntake,
        }),
      } : undefined,

      // Chart state (persist note sections and user feedback together)
      chart:
        (chartHook.noteSections &&
          Object.keys(chartHook.noteSections).length > 0) ||
        chartHook.feedback
          ? {
              ...(chartHook.noteSections && {
                noteSections: chartHook.noteSections,
              }),
              ...(chartHook.feedback && {
                feedback: chartHook.feedback,
              }),
              ...sourceFields(chartHook),
            }
          : undefined,
    };

    // Remove all undefined/empty values recursively
    return removeEmptyValues(raw);
  }, [
    currentStep,
    recordingHook.transcript,
    diagnosisHook.ddxData,
    diagnosisHook.clinicalReferences,
    diagnosisHook.embeddedDocuments,
    selectedDiagnoses,
    customDiagnoses,
    clinicianImpression,
    disabledDiagnosisIds,
    diagnosticPlanHook.planData,
    disabledTestIds,
    testResults,
    confirmDiagnosisHook.confirmedData,
    selectedConfirmedDiagnoses,
    treatmentPlanHook.planData,
    treatmentPlanHook.clinicalReferences,
    treatmentPlanHook.embeddedDocuments,
    customTreatments,
    disabledTreatmentIds,
    selectedTreatments,
    dischargeHook.instructions,
    dischargeHook.includedInstructionIds,
    dischargeHook.patientName,
    dischargeHook.date,
    dischargeHook.hasBeenGenerated,
    dischargeHook.clinicalReferences,
    dischargeHook.embeddedDocuments,
    chartHook.noteSections,
    chartHook.feedback,
    chartHook.clinicalReferences,
    chartHook.embeddedDocuments,
    patientContext,
    inpatient,
    encounterSetting,
    noteFormat,
    specialty,
    inpatientEvents,
    dailyPlanComments,
    dailyPlanReassuring,
    pearls,
    pearlsSubject,
  ]);

  // ============================================================================
  // AUTO-SAVE LOGIC
  // ============================================================================

  /**
   * Check if session data has changed since last save
   * Uses JSON.stringify for deep comparison (safe because data is JSON-serializable)
   */
  const hasDataChanged = useCallback((currentData) => {
    if (!lastSavedSnapshotRef.current) return true;
    
    const currentJson = JSON.stringify(currentData);
    const lastSavedJson = JSON.stringify(lastSavedSnapshotRef.current);
    
    return currentJson !== lastSavedJson;
  }, []);

  /**
   * Auto-save session to Firestore (only if data changed)
   * This is called from three places:
   * 1. Immediately after AI workflow completes
   * 2. After 3s debounce when user edits data
   * 3. When navigating between steps
   * 
   * Guards against concurrent saves to prevent race conditions
   * 
   * @param {string} source - Debug label for where this save was triggered from
   */
  const autoSaveSession = useCallback(async (source = 'unknown') => {
    // [BETA-KIJABE] Noop all saves for Kijabe beta testers — remove when beta ends
    if (disableSave) return;

    if (!user?.uid) return;
    
    // CRITICAL: Prevent auto-save during hydration (blocks all paths: AI callbacks + debounced edits)
    if (isHydratingRef.current) return;
    
    // CRITICAL: Prevent concurrent saves (race condition guard)
    if (isSavingRef.current) return;
    
    // CRITICAL: Don't save blank encounters - require at least a transcript (or, on the Events
    // note, an event)
    if (!sourceText || sourceText.trim().length === 0) return;
    
    const currentData = serializeSession();
    
    // Skip if no meaningful data yet (empty object)
    if (!currentData || Object.keys(currentData).length === 0) return;
    
    // Skip if data unchanged
    if (!hasDataChanged(currentData)) return;
    
    try {
      // Set lock BEFORE any async operations
      isSavingRef.current = true;
      setSaving(true);
      
      // Create session ID if this is first save
      const isFirstSave = !sessionId;
      const id =
        sessionId ||
        (await sessionService.createNewSession(user.uid, reportingMetadata));
      
      // Generate title on first save (background - non-blocking)
      if (isFirstSave && sourceText) {
        sessionService.generateSessionTitle(id, sourceText)
          .catch(err => console.warn('[AutoSave] Title generation failed:', err.message));
      }
      
      // Save to Firestore
      await sessionService.saveSession(
        id,
        currentData,
        user.uid,
        reportingMetadata,
      );
      
      // Set as active session if new
      if (isFirstSave) {
        setSessionId(id);
        await sessionService.setActiveSessionId(user.uid, id);
      }
      
      // Update snapshot and clear unsaved flag
      lastSavedSnapshotRef.current = currentData;
      setHasUnsavedChanges(false);
      setLastSavedAt(new Date());
      
      return id;
    } catch (error) {
      console.error('[AutoSave] Save failed:', error);
      setSessionError(error.message);
    } finally {
      // Release lock AFTER everything completes
      isSavingRef.current = false;
      setSaving(false);
    }
  }, [
    user?.uid,
    sessionId,
    serializeSession,
    hasDataChanged,
    sourceText,
    reportingMetadata,
  ]);

  // Create debounced version for navigation saves (3s delay to catch accidental clicks)
  const debouncedAutoSaveNavigation = useDebouncedCallback(
    (source) => autoSaveSession(source),
    3000,
    { leading: false, trailing: true }
  );

  // Wire up the refs to autoSaveSession after it's defined
  useEffect(() => {
    onDataChangedRef.current = () => autoSaveSession('ai-callback');
    autoSaveSessionRef.current = (source) => debouncedAutoSaveNavigation(source || 'ref');
  }, [autoSaveSession, debouncedAutoSaveNavigation]);

  // Keep serializeSessionRef current so loadSession's setTimeout can call the latest version
  useEffect(() => {
    serializeSessionRef.current = serializeSession;
  }, [serializeSession]);

  /**
   * Create a snapshot of current session for debounced save detection
   * This tracks all user-editable state
   */
  const currentSessionSnapshot = useMemo(() => 
    JSON.stringify(serializeSession())
  , [
    serializeSession,
    // Navigation state
    currentStep,
    // Recording state
    recordingHook.transcript,
    // Diagnosis state
    diagnosisHook.ddxData,
    selectedDiagnoses,
    customDiagnoses,
    clinicianImpression,
    // Diagnostic plan state
    diagnosticPlanHook.planData,
    disabledTestIds,
    testResults,
    // Confirmed diagnosis state
    confirmDiagnosisHook.confirmedData,
    selectedConfirmedDiagnoses,
    // Treatment state
    treatmentPlanHook.planData,
    customTreatments,
    disabledTreatmentIds,
    selectedTreatments,
    // Discharge state
    dischargeHook.instructions,
    dischargeHook.includedInstructionIds,
    dischargeHook.patientName,
    dischargeHook.date,
    dischargeHook.hasBeenGenerated,
    // Chart state
    chartHook.noteSections,
    chartHook.feedback,
    // Inpatient state
    encounterSetting,
    noteFormat,
    specialty,
    inpatientEvents,
    dailyPlanComments,
    dailyPlanReassuring,
    pearls,
  ]);

  // Debounce the snapshot for 3s (returns [value, { cancel }]) — only delays Firestore writes, not the dirty flag
  const [debouncedSnapshot, debouncedSnapshotControl] = useDebounce(currentSessionSnapshot, 3000);

  /**
   * Dirty flag: immediate (no debounce). Compare current serialized state to last successful save.
   */
  useEffect(() => {
    if (isHydratingRef.current) return;
    if (!sourceText || sourceText.trim().length === 0) {
      setHasUnsavedChanges(false);
      return;
    }
    const last = lastSavedSnapshotRef.current;
    const lastJson = last ? JSON.stringify(last) : null;
    if (!lastJson) {
      const data = serializeSession();
      const hasData = data && Object.keys(data).length > 0;
      setHasUnsavedChanges(!!hasData);
      return;
    }
    setHasUnsavedChanges(currentSessionSnapshot !== lastJson);
  }, [currentSessionSnapshot, sourceText, serializeSession]);

  /**
   * Trigger auto-save when debounced snapshot changes (user edit detection)
   */
  useEffect(() => {
    if (!debouncedSnapshot) return;
    if (isHydratingRef.current) return; // Skip during hydration
    
    const lastSavedJson = lastSavedSnapshotRef.current 
      ? JSON.stringify(lastSavedSnapshotRef.current) 
      : null;
    
    if (debouncedSnapshot !== lastSavedJson) {
      autoSaveSessionRef.current?.('debounced-edit');
    }
  }, [debouncedSnapshot]);

  /**
   * Hydrate session state from serialized data
   * Restores state into all hooks and local state
   * Navigation is restored to the saved step (if valid), otherwise starts from the last step with data
   */
  const hydrateSession = useCallback((data) => {
    if (!data) return;

    try {
      // Restore navigation state if available
      const savedStep = data.navigation?.currentStep;
      
      // Determine which steps should be marked as visited based on saved data
      const stepsToVisit = new Set([STEPS.RECORD]); // Always start with RECORD
      
      // Mark steps as visited based on what data exists
      if (data.diagnosis?.ddxData || data.diagnosis?.selectedDiagnosisNames) {
        stepsToVisit.add(STEPS.DIAGNOSIS);
      }
      if (data.pearls?.pearls?.length) {
        stepsToVisit.add(STEPS.PEARLS);
      }
      if (data.diagnosticPlan?.planData) {
        stepsToVisit.add(STEPS.DIAGNOSTIC_PLAN);
      }
      if (data.confirmDiagnosis?.confirmedData || data.confirmDiagnosis?.selectedConfirmedDiagnosisNames) {
        stepsToVisit.add(STEPS.CONFIRM_DIAGNOSIS);
      }
      if (data.treatment?.planData) {
        stepsToVisit.add(STEPS.TREATMENT);
      }
      const savedInpatient = isInpatient(data.encounterSetting);
      // The Event step is the Events note's first step, so it is always open there.
      const savedEventsNote = isEventsNote(data.encounterSetting, data.inpatient?.noteFormat);
      if (savedEventsNote) {
        stepsToVisit.add(STEPS.EVENTS);
      }
      if (data.discharge?.instructions || data.discharge?.patientName) {
        stepsToVisit.add(STEPS.DISCHARGE);
      }
      if (data.chart?.noteSections) {
        stepsToVisit.add(STEPS.CHART);
      }
      
      // Determine which step to start on
      let startingStep;
      if (savedStep && stepsToVisit.has(savedStep)) {
        // If we have a saved step and it's valid (has data), use it
        startingStep = savedStep;
      } else {
        // Otherwise, determine the furthest step with data (last accessible step)
        // Event is the Events note's first step, so it sits where Record does.
        const stepOrder = [STEPS.RECORD, STEPS.EVENTS, STEPS.DIAGNOSIS, STEPS.PEARLS, STEPS.DIAGNOSTIC_PLAN, STEPS.CONFIRM_DIAGNOSIS, STEPS.TREATMENT, STEPS.DISCHARGE, STEPS.CHART];
        startingStep = STEPS.RECORD;
        
        for (const step of stepOrder) {
          if (stepsToVisit.has(step)) {
            startingStep = step;
          }
        }
      }
      
      // Start from the determined step
      setCurrentStep(startingStep);
      setVisitedSteps(stepsToVisit);

      // The session's own setting, without touching the user's default for new encounters.
      setEncounterSettingState(normalizeEncounterSetting(data.encounterSetting));
      setSpecialtyState(data.specialty ? normalizeSpecialty(data.specialty) : readDefaultSpecialty());
      setNoteFormatState(
        savedInpatient ? normalizeNoteFormat(data.inpatient?.noteFormat) : readDefaultNoteFormat(),
      );
      const savedEvents = normalizeInpatientEvents(data.inpatient?.events);
      setInpatientEvents(savedEvents);
      eventsDdxSourceRef.current =
        savedEventsNote && data.diagnosis?.ddxData ? buildEventsSourceText(savedEvents) : "";
      setDailyPlanComments(data.inpatient?.dailyPlanComments || {});
      setDailyPlanReassuring(data.inpatient?.dailyPlanReassuring || {});
      setPearls(data.pearls?.pearls || []);
      setPearlsSubject(data.pearls?.subject || "");

      // Restore recording (transcript only - can't restore MediaRecorder state)
      if (data.recording?.transcript) {
        recordingHook.updateTranscript(data.recording.transcript);
      }

      // Restore imported patient context. `record` / `careStartResult` are present only if
      // the session was saved with PERSIST_IMPORTED_PATIENT_RECORD enabled; spreading the
      // stored object restores them when they are there and leaves identity-only otherwise.
      // Identity-only sets patientContext.recordUnavailable, which prompts a re-import
      // instead of letting the steps run without the record.
      if (data.patientContext?.patientUserId) {
        setPatientContext(data.patientContext);
      }

      // Restore diagnosis
      diagnosisHook.hydrateSourceDocuments(data.diagnosis);
      if (data.diagnosis) {
        // Restore ddxData into diagnosis hook
        if (data.diagnosis.ddxData) {
          diagnosisHook.setDdxData(data.diagnosis.ddxData);
        }

        const customDx = data.diagnosis.customDiagnoses || [];
        setCustomDiagnoses(customDx);

        if (data.diagnosis.clinicianImpression) {
          setClinicianImpression(data.diagnosis.clinicianImpression);
        }
        
        // Reconstruct selectedDiagnoses from names (include user-added diagnoses in lookup)
        if (data.diagnosis.selectedDiagnosisNames) {
          const allDiagnoses = [
            ...(data.diagnosis.ddxData?.primary_diagnoses || []),
            ...(data.diagnosis.ddxData?.alternative_diagnoses || []),
            ...customDx,
          ];
          const reconstructed = data.diagnosis.selectedDiagnosisNames
            .map(name => allDiagnoses.find(d => (d.condition || d.name) === name))
            .filter(Boolean); // Remove any that weren't found
          setSelectedDiagnoses(reconstructed);
        }
        // Fallback for old format (full objects stored)
        else if (data.diagnosis.selectedDiagnoses) {
          setSelectedDiagnoses(data.diagnosis.selectedDiagnoses);
        }
        
        // Restore disabled diagnosis IDs
        if (data.diagnosis.disabledDiagnosisIds) {
          setDisabledDiagnosisIds(new Set(data.diagnosis.disabledDiagnosisIds));
        }
      }

      // Restore diagnostic plan
      if (data.diagnosticPlan) {
        // Restore plan data into hook
        if (data.diagnosticPlan.planData) {
          diagnosticPlanHook.setPlanData(data.diagnosticPlan.planData);
        }
        
        // Restore UI state
        if (data.diagnosticPlan.disabledTestIds) {
          setDisabledTestIds(new Set(data.diagnosticPlan.disabledTestIds));
        }
        if (data.diagnosticPlan.testResults) {
          // Handle both old format (array of entries) and new format (object)
          const testResultsData = Array.isArray(data.diagnosticPlan.testResults)
            ? data.diagnosticPlan.testResults // Old format: [[key, value], ...]
            : Object.entries(data.diagnosticPlan.testResults); // New format: {key: value, ...}
          setTestResults(new Map(testResultsData));
        }
      }

      // Restore confirmed diagnosis
      if (data.confirmDiagnosis) {
        // Restore confirmedData into confirm diagnosis hook
        if (data.confirmDiagnosis.confirmedData) {
          confirmDiagnosisHook.setConfirmedData(data.confirmDiagnosis.confirmedData);
        }
        
        // Reconstruct selectedConfirmedDiagnoses from names by looking up in confirmedData
        // Include `diagnoses` — new schema from useConfirmDiagnosis (not only legacy primary/alternative arrays)
        if (data.confirmDiagnosis.selectedConfirmedDiagnosisNames && data.confirmDiagnosis.confirmedData) {
          const cd = data.confirmDiagnosis.confirmedData;
          const allConfirmedDiagnoses = [
            ...(cd.confirmed_diagnoses || []),
            ...(cd.alternative_diagnoses || []),
            ...(cd.diagnoses || []),
          ];
          const names = data.confirmDiagnosis.selectedConfirmedDiagnosisNames;
          let reconstructed = names
            .map(name => allConfirmedDiagnoses.find(d => (d.condition || d.name) === name))
            .filter(Boolean);
          // If nothing matched (name drift / schema quirks) but names were saved, keep gating state consistent
          if (reconstructed.length === 0 && names.length > 0) {
            reconstructed = names.map((name) => ({
              condition: name,
              likelihood: 'Likely',
              rationale: '',
            }));
          }
          setSelectedConfirmedDiagnoses(reconstructed);
        }
        // Fallback for old format (full objects stored)
        else if (data.confirmDiagnosis.selectedConfirmedDiagnoses) {
          setSelectedConfirmedDiagnoses(data.confirmDiagnosis.selectedConfirmedDiagnoses);
        }
      }

      // Restore treatment
      treatmentPlanHook.hydrateSourceDocuments(data.treatment);
      if (data.treatment) {
        // Restore plan data into hook
        if (data.treatment.planData) {
          treatmentPlanHook.setPlanData(data.treatment.planData);
        }
        
        // Restore UI state
        if (data.treatment.customTreatments) {
          setCustomTreatments(data.treatment.customTreatments);
        }
        if (data.treatment.disabledTreatmentIds) {
          setDisabledTreatmentIds(new Set(data.treatment.disabledTreatmentIds));
        }
        if (data.treatment.selectedTreatments) {
          setSelectedTreatments(new Set(data.treatment.selectedTreatments));
        }
      }

      // Restore discharge
      if (data.discharge) {
        dischargeHook.hydrateDischargeData(data.discharge);
      }

      // Restore chart
      chartHook.setNoteSections(data.chart?.noteSections || null);
      chartHook.setFeedback(data.chart?.feedback || null);
      chartHook.hydrateSourceDocuments(data.chart);
    } catch (err) {
      console.error('[useChartMindSession] Error hydrating session:', err);
      throw new Error('Failed to restore session data');
    }
  }, [
    recordingHook.updateTranscript,
    diagnosisHook.setDdxData,
    diagnosisHook.hydrateSourceDocuments,
    diagnosticPlanHook.setPlanData,
    confirmDiagnosisHook.setConfirmedData,
    treatmentPlanHook.setPlanData,
    treatmentPlanHook.hydrateSourceDocuments,
    dischargeHook.hydrateDischargeData,
    chartHook.setNoteSections,
    chartHook.setFeedback,
    chartHook.hydrateSourceDocuments,
    setCurrentStep,
    setVisitedSteps,
    setSelectedDiagnoses,
    setDisabledTestIds,
    setTestResults,
    setSelectedConfirmedDiagnoses,
    setDisabledTreatmentIds,
    setSelectedTreatments,
    setPatientContext,
  ]);

  // ============================================================================
  // SESSION PERSISTENCE (Phase 3+4)
  // ============================================================================

  /**
   * Save current session to Firestore
   */
  const saveSession = useCallback(async () => {
    // [BETA-KIJABE] Noop manual save for Kijabe beta testers — remove when beta ends
    if (disableSave) return;

    if (!user?.uid) {
      setSessionError('Must be logged in to save session');
      throw new Error('User not authenticated');
    }

    setSaving(true);
    setSessionError(null);

    try {
      const data = serializeSession();
      const id =
        sessionId ||
        (await sessionService.createNewSession(user.uid, reportingMetadata));
      
      await sessionService.saveSession(id, data, user.uid, reportingMetadata);
      await sessionService.setActiveSessionId(user.uid, id);
      
      setSessionId(id);
      return id;
    } catch (error) {
      console.error('[useChartMindSession] Error saving session:', error);
      setSessionError(error.message);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [user?.uid, sessionId, serializeSession, reportingMetadata]);

  /**
   * Load session from Firestore
   */
  const loadSession = useCallback(async (id) => {
    if (!id) {
      throw new Error('Session ID is required');
    }

    setLoading(true);
    setSessionError(null);

    try {
      const session = await sessionService.loadSession(id);
      
      if (!session) {
        throw new Error('Session not found');
      }

      // Note: isHydratingRef and cancel() are already set in the auto-load effect
      // This is a backup for manual loadSession() calls
      if (!isHydratingRef.current) {
        isHydratingRef.current = true;
        debouncedSnapshotControl.cancel();
      }
      
      hydrateSession(session.data);
      setSessionId(id);
      
      // Wait one render cycle for React to flush all batched state updates from hydrateSession,
      // then capture the hydrated state as the baseline. Use serializeSessionRef (not the closure
      // value of serializeSession) so we get the post-hydration snapshot, not the pre-hydration one.
      setTimeout(() => {
        const currentState = serializeSessionRef.current?.() ?? serializeSession();
        lastSavedSnapshotRef.current = currentState;
        setHasUnsavedChanges(false);
        isHydratingRef.current = false;
        // Catch up on any edits the user made while hydration was blocking saves
        autoSaveSessionRef.current?.('post-hydration');
      }, 300);
      
      return session;
    } catch (error) {
      console.error('[useChartMindSession] Error loading session:', error);
      setSessionError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [hydrateSession, serializeSession, debouncedSnapshotControl]);

  /**
   * Clear current session state (for starting fresh)
   */
  const clearSession = useCallback(() => {
    
    // Reset session metadata
    setSessionId(null);
    setHasLoadedSession(false);
    setSessionError(null);
    setHasUnsavedChanges(false);
    
    // Reset navigation
    setCurrentStep(STEPS.RECORD);
    setVisitedSteps(new Set([STEPS.RECORD]));
    
    // Reset all hook state
    recordingHook.updateTranscript('');
    clarifyingQuestionsHook.clear();
    diagnosisHook.clearDDX();
    diagnosticPlanHook.clearPlan();
    confirmDiagnosisHook.clearConfirmedDiagnoses();
    treatmentPlanHook.clearPlan();
    dischargeHook.reset();
    chartHook.clearNote();
    
    // Reset local state
    setSelectedDiagnoses([]);
    setCustomDiagnoses([]);
    setClinicianImpression(null);
    setDisabledDiagnosisIds(new Set());
    setDisabledTestIds(new Set());
    setTestResults(new Map());
    setSelectedConfirmedDiagnoses([]);
    setCustomTreatments([]);
    setDisabledTreatmentIds(new Set());
    setSelectedTreatments(new Set());
    setPatientContext(null);
    setEncounterSettingState(readDefaultEncounterSetting());
    setNoteFormatState(readDefaultNoteFormat());
    setSpecialtyState(readDefaultSpecialty());
    setInpatientEvents([]);
    eventsDdxSourceRef.current = "";
    setDailyPlanComments({});
    setDailyPlanReassuring({});
    setTextMode(false);
    pearlsRequestRef.current += 1;
    setPearls([]);
    setPearlsSubject("");
    setPearlsError("");
    setPearlsLoading(false);
    
    // Reset refs
    userNavigatedRef.current = false;
    // Keep analysisTriggeredRef true so the auto-analysis effect doesn't immediately
    // re-fire with the stale debouncedTranscript. The transcript-change effect
    // (deps: [recordingHook.transcript]) will flip it back to false once the clear lands.
    analysisTriggeredRef.current = true;
    generatedForRef.current = null;
    lastSavedSnapshotRef.current = null;
  }, [
    recordingHook,
    clarifyingQuestionsHook,
    diagnosisHook,
    diagnosticPlanHook,
    confirmDiagnosisHook,
    treatmentPlanHook,
    dischargeHook,
    chartHook,
  ]);

  /**
   * Create a new session (clears current state)
   */
  const createNewSessionHandler = useCallback(async () => {
    if (!user?.uid) {
      throw new Error('User not authenticated');
    }

    try {
      clearSession(); // Clear all state first
      
      const id = await sessionService.createNewSession(
        user.uid,
        reportingMetadata,
      );
      await sessionService.setActiveSessionId(user.uid, id);
      setSessionId(id);
      
      return id;
    } catch (error) {
      console.error('[useChartMindSession] Error creating session:', error);
      setSessionError(error.message);
      throw error;
    }
  }, [user?.uid, clearSession, reportingMetadata]);

  // Auto-load session on mount if initialSessionId provided
  useEffect(() => {
    async function loadInitialSession() {
      if (!user?.uid) {
        return;
      }
      
      // If initialSessionId provided and we haven't loaded it yet, load that session
      if (initialSessionId && !hasLoadedSession) {
        // Note: isHydratingRef already starts as true (initialized in useState above)
        debouncedSnapshotControl.cancel();
        
        setHasLoadedSession(true); // Set flag immediately to prevent duplicate loads
        try {
          await loadSession(initialSessionId);
        } catch (error) {
          console.error('[useChartMindSession] Failed to auto-load session:', error);
          setHasLoadedSession(false); // Reset on error so they can retry
          isHydratingRef.current = false; // Re-enable saves on error
        }
      }
    }

    loadInitialSession();
  }, [initialSessionId, user?.uid, loadSession, hasLoadedSession, debouncedSnapshotControl]);

  // ============================================================================
  // RETURN GROUPED STATE
  // ============================================================================

  return {
    // Navigation
    navigation: {
      currentStep,
      visitedSteps,
      setCurrentStep: handleStepChange,
      handleContinueToDiagnosis,
      handleContinueToPearls,
      handleContinueToPlan,
      handleContinueToConfirm,
      handleContinueToTreatment,
      handleContinueToDischarge,
      handleContinueToChart,
      handleSkipToChart,
      handleBack,
    },

    // Inpatient or outpatient, plus what only inpatient encounters collect
    encounter: {
      setting: encounterSetting,
      inpatient,
      setSetting: setEncounterSetting,
      noteFormat,
      setNoteFormat,
      specialty,
      setSpecialty,
      eventsNote,
      // What the differential, plan and note are written from (transcript, or the events).
      sourceText,
      planSourceText,
      events: inpatientEvents,
      addEvent: handleAddInpatientEvent,
      updateEvent: handleUpdateInpatientEvent,
      removeEvent: handleRemoveInpatientEvent,
      dailyPlanComments,
      dailyPlanReassuring,
      setDailyPlanComment: handleDailyPlanCommentChange,
      setDailyPlanReassuring: handleSetDailyPlanReassuring,
    },

    // Recording
    recording: {
      ...recordingHook,
      debouncedTranscript,
      onMicClick: handleMicClick,
      language: recordingLanguage,
      setLanguage: setRecordingLanguage,
      pasteClarifying,
      setPasteClarifying,
      textMode,
      setTextMode,
      // True while EITHER device is capturing. `isRecording` above stays honest about this
      // device's own microphone, because it also drives the mic button's stop icon and the
      // transcript field's readOnly.
      captureActive,
      phone: phoneRelay,
      // Dictating one note section from the paired phone. Separate from `phone` because it is a
      // different destination for the same wire, not a different wire.
      phoneDictation: {
        // A phone has actually scanned and is on the other end, so offering this is honest.
        available: Boolean(phoneRelay.phoneClaimedAt),
        target: phoneDictation,
        text: phoneDictationText,
        recording: isPhoneRecording && Boolean(phoneDictation),
        begin: beginPhoneDictation,
        cancel: cancelPhoneDictation,
      },
    },

    // Imported patient CMR (from share code). Every step's generator is fed `payload`,
    // which callLLMAPI renders into the system prompt as the authoritative source for
    // meds / allergies / PMH.
    patientContext: {
      value: patientContext,
      payload: patientContextPayload,
      recordUnavailable: patientRecordUnavailable,
      set: setPatientContext,
      clear: clearPatientContext,
    },

    // Clarifying questions (live-polled while recording)
    clarifyingQuestions: {
      ...clarifyingQuestionsHook,
      enabled: clarifyingQuestionsEnabled,
    },

    // Diagnosis
    //
    // Every retry below is wrapped to inject the patient context. The step components call
    // retry with just the transcript and diagnoses, so without these wrappers a single
    // retry click silently drops the imported record and regenerates without it.
    diagnosis: {
      ...diagnosisHook,
      selectedDiagnoses,
      customDiagnoses,
      clinicianImpression,
      onRecordClinicianImpression: handleRecordClinicianImpression,
      onAddCustomDiagnosis: handleAddCustomDiagnosis,
      disabledDiagnosisIds,
      onSelectionChange: handleSelectionChange,
      onToggleDiagnosisDisabled: handleToggleDiagnosisDisabled,
      onPrefetch: handlePrefetch,
      retry: (transcript) => {
        diagnosisHook.retry(transcript, patientContextPayload);
      },
    },

    // Diagnostic Plan
    diagnosticPlan: {
      ...diagnosticPlanHook,
      disabledTestIds,
      testResults,
      onToggleTestDisabled: handleToggleTestDisabled,
      onUpdateTestResult: handleUpdateTestResult,
      retry: (transcript, diagnoses) => {
        diagnosticPlanHook.retry(transcript, diagnoses, patientContextPayload);
      },
    },

    // Confirmed Diagnosis
    confirmDiagnosis: {
      ...confirmDiagnosisHook,
      selectedConfirmedDiagnoses,
      onSelectionChange: handleConfirmedSelectionChange,
      originalDdxData: diagnosisHook.ddxData, // Pass original DDX as seed
      retry: (transcript, testResults, originalDdxData) => {
        confirmDiagnosisHook.retry(
          transcript,
          testResults,
          originalDdxData,
          disabledDiagnosisIds,
          patientContextPayload,
        );
      },
    },

    // Teaching pearls
    pearls: {
      pearls,
      subject: pearlsSubject,
      loading: pearlsLoading,
      error: pearlsError,
      available: !pearlsUnavailable,
      retry: () => {
        setPearlsError("");
        void runPearls(pearlsTarget);
      },
    },

    // Treatment Plan
    treatment: {
      ...treatmentPlanHook,
      customTreatments,
      onAddCustomTreatment: handleAddCustomTreatment,
      disabledTreatmentIds,
      selectedTreatments,
      onToggleTreatmentDisabled: handleToggleTreatmentDisabled,
      onToggleTreatmentSelected: handleToggleTreatmentSelected,
      retry: (transcript, diagnoses, diagnosticPlanData) => {
        treatmentPlanHook.retry(
          transcript,
          diagnoses,
          diagnosticPlanData,
          patientContextPayload,
        );
      },
    },

    // Discharge
    discharge: {
      ...dischargeHook,
    },

    // Chart
    chart: {
      ...chartHook,
      onCopyToClipboard: handleCopyToClipboard,
      onExportToPdf: handleExportToPdf,
      onRegenerate: handleRegenerate,
      isStale: isNoteStale,
      // The key already covers diagnoses, so a note showing with none selected was necessarily
      // generated without them. Drives the "add more detail" prompt on the Chart step.
      generatedWithoutDiagnoses: chartHook.hasNote && selectedDiagnoses.length === 0,
    },

    // Session management
    session: {
      sessionId,
      saving,
      loading,
      error: sessionError,
      hasUnsavedChanges,
      lastSavedAt,
      saveSession,
      loadSession,
      createNewSession: createNewSessionHandler,
      clearSession,
      serializeSession,
      hydrateSession,
      autoSaveSession,
    },
  };
}

export default useChartMindSession;
